import { useEffect, useMemo, useRef, useState } from "react";
import { BsTrash } from "react-icons/bs";
import { IoAdd } from "react-icons/io5";
import { MdChevronRight, MdDragIndicator, MdExpandMore } from "react-icons/md";
import {
	getCatalogFieldNames,
	getCatalogSectionNames,
	getFieldLatestVersion,
	getSectionLatestVersion,
	getTemplateDetails,
	getTemplateLatestVersion,
} from "./catalogOrder";

const TEMPLATE_CHIPS = [
	{ label: "Type", className: "bg-sky-100 text-sky-800" },
	{ label: "Portfolio", className: "bg-amber-100 text-amber-900" },
	{ label: "Model", className: "bg-purple-100 text-purple-800" },
];

const VersionBadge = ({ version }) => {
	if (version == null) return null;
	return (
		<span
			className="shrink-0 text-[10px] font-medium text-stone-500 bg-stone-100 border border-stone-200 rounded px-1 py-0.5 tabular-nums"
			title={`Latest template version that includes this: v${version}`}>
			v{version}
		</span>
	);
};

export const isTemplateIncluded = (dataToSave, templateName) => {
	const t = dataToSave?.templateData?.[templateName];
	return !!(t && t.deleted !== true);
};

export const isSectionIncluded = (dataToSave, templateName, sectionName) => {
	const t = dataToSave?.templateData?.[templateName];
	if (!t || t.deleted === true) return false;
	return (t.includedSections || []).some((s) => s.name === sectionName && s.deleted !== true);
};

export const isFieldIncluded = (dataToSave, templateName, sectionName, fieldName) => {
	const t = dataToSave?.templateData?.[templateName];
	if (!t || t.deleted === true) return false;
	return (t.includedFields?.[sectionName] || []).some((f) => f.name === fieldName && f.deleted !== true);
};

/** Included templates in tree / object-key order (not alphabetical). */
export const getIncludedTemplateNames = (dataToSave) =>
	Object.keys(dataToSave?.templateData || {}).filter((name) => dataToSave.templateData[name]?.deleted !== true);

/**
 * Reorder included templates by drag index. Soft-deleted keys stay after the active list.
 * `toIndex` is the drop slot in the included list (0..length); same pattern as cover layers.
 */
export const reorderTemplates = (dataToSave, fromIndex, toIndex) => {
	const names = getIncludedTemplateNames(dataToSave);
	if (fromIndex < 0 || fromIndex >= names.length) return dataToSave;
	let insertAt = toIndex;
	if (fromIndex < insertAt) insertAt -= 1;
	if (insertAt < 0) insertAt = 0;
	if (fromIndex === insertAt) return dataToSave;

	const ordered = [...names];
	const [moved] = ordered.splice(fromIndex, 1);
	ordered.splice(insertAt, 0, moved);

	const prev = dataToSave.templateData || {};
	const next = {};
	ordered.forEach((name) => {
		next[name] = prev[name];
	});
	Object.keys(prev).forEach((name) => {
		if (!(name in next)) next[name] = prev[name];
	});
	return { ...dataToSave, templateData: next };
};

/**
 * Add a template at the end (add order). Re-adding a soft-deleted one moves it to the end.
 */
export const addTemplate = (dataToSave, templateName) => {
	if (!templateName || !String(templateName).trim()) return dataToSave;
	const name = String(templateName).trim();
	const templateData = { ...(dataToSave.templateData || {}) };
	const existing = templateData[name];

	if (existing && existing.deleted !== true) {
		return dataToSave;
	}

	if (existing && existing.deleted === true) {
		const { [name]: removed, ...rest } = templateData;
		return {
			...dataToSave,
			templateData: {
				...rest,
				[name]: { ...removed, deleted: false },
			},
		};
	}

	return {
		...dataToSave,
		templateData: {
			...templateData,
			[name]: {
				id: -1,
				deleted: false,
				from_server: false,
				includedSections: [],
				includedFields: {},
			},
		},
	};
};

/** Soft-delete or remove template (and cascade sections/fields like classic builder). */
export const removeTemplate = (dataToSave, templateName) => {
	const templateData = { ...(dataToSave.templateData || {}) };
	const existing = templateData[templateName];
	if (!existing) return dataToSave;

	if (existing.from_server) {
		const includedSections = [...(existing.includedSections || [])];
		for (let i = includedSections.length - 1; i >= 0; i--) {
			if (includedSections[i].from_server) includedSections[i] = { ...includedSections[i], deleted: true };
			else includedSections.splice(i, 1);
		}
		const includedFields = { ...(existing.includedFields || {}) };
		Object.keys(includedFields).forEach((sectionName) => {
			const list = [...(includedFields[sectionName] || [])];
			for (let j = list.length - 1; j >= 0; j--) {
				if (list[j].from_server) list[j] = { ...list[j], deleted: true };
				else list.splice(j, 1);
			}
			includedFields[sectionName] = list;
		});
		templateData[templateName] = {
			...existing,
			deleted: true,
			includedSections,
			includedFields,
		};
	} else {
		delete templateData[templateName];
	}

	return { ...dataToSave, templateData };
};

export const toggleSection = (dataToSave, templateName, sectionName) => {
	if (!isTemplateIncluded(dataToSave, templateName)) return dataToSave;

	const templateData = { ...dataToSave.templateData };
	const template = { ...templateData[templateName] };
	const sections = [...(template.includedSections || [])];
	const fields = { ...(template.includedFields || {}) };
	const idx = sections.findIndex((s) => s.name === sectionName);
	const currentlyOn = idx >= 0 && sections[idx].deleted !== true;

	if (currentlyOn) {
		if (sections[idx].from_server) {
			sections[idx] = { ...sections[idx], deleted: true };
		} else {
			sections.splice(idx, 1);
		}
		const existingFields = fields[sectionName] || [];
		const nextFields = [];
		existingFields.forEach((f) => {
			if (f.from_server) nextFields.push({ ...f, deleted: true });
		});
		if (nextFields.length) fields[sectionName] = nextFields;
		else delete fields[sectionName];
	} else if (idx >= 0) {
		sections[idx] = { ...sections[idx], deleted: false };
	} else {
		sections.push({ id: -1, name: sectionName, from_server: false, deleted: false });
		if (!fields[sectionName]) fields[sectionName] = [];
	}

	template.includedSections = sections;
	template.includedFields = fields;
	templateData[templateName] = template;
	return { ...dataToSave, templateData };
};

export const toggleField = (dataToSave, templateName, sectionName, fieldName) => {
	let next = dataToSave;
	if (!isSectionIncluded(next, templateName, sectionName)) {
		next = toggleSection(next, templateName, sectionName);
	}
	if (!isTemplateIncluded(next, templateName)) return dataToSave;

	const templateData = { ...next.templateData };
	const template = { ...templateData[templateName] };
	const fieldsBySection = { ...(template.includedFields || {}) };
	const list = [...(fieldsBySection[sectionName] || [])];
	const idx = list.findIndex((f) => f.name === fieldName);
	const currentlyOn = idx >= 0 && list[idx].deleted !== true;

	if (currentlyOn) {
		if (list[idx].from_server) list[idx] = { ...list[idx], deleted: true };
		else list.splice(idx, 1);
	} else if (idx >= 0) {
		list[idx] = { ...list[idx], deleted: false };
	} else {
		list.push({ id: -1, name: fieldName, from_server: false, deleted: false });
	}

	fieldsBySection[sectionName] = list;
	template.includedFields = fieldsBySection;
	templateData[templateName] = template;
	return { ...next, templateData };
};

const ReportStructureTree = ({ catalog, dataToSave, onChange, focusKey, focusNonce, onFocus }) => {
	const [expanded, setExpanded] = useState(() => new Set());
	const [showAddTemplate, setShowAddTemplate] = useState(false);
	const [templateQuery, setTemplateQuery] = useState("");
	const [dragTemplateName, setDragTemplateName] = useState(null);
	const [dropIndex, setDropIndex] = useState(null);
	const dragTemplateNameRef = useRef(null);
	const dropIndexRef = useRef(null);
	const treeScrollRef = useRef(null);
	const searchRef = useRef(null);

	const includedTemplates = useMemo(() => getIncludedTemplateNames(dataToSave), [dataToSave]);

	const beginTemplateDrag = (templateName) => {
		dragTemplateNameRef.current = templateName;
		dropIndexRef.current = null;
		setDragTemplateName(templateName);
		setDropIndex(null);
	};

	const updateDropIndex = (nextIndex) => {
		if (nextIndex == null || Number.isNaN(nextIndex)) return;
		dropIndexRef.current = nextIndex;
		setDropIndex(nextIndex);
	};

	/** Insert-before index from pointer Y (top half = before, bottom half = after). */
	const slotForPointer = (event, index) => {
		const rect = event.currentTarget.getBoundingClientRect();
		const after = event.clientY > rect.top + rect.height / 2;
		return after ? index + 1 : index;
	};

	/** Map a Y position to a drop slot using the current template row boxes. */
	const slotForClientY = (clientY) => {
		const root = treeScrollRef.current;
		if (!root) return dropIndexRef.current;
		const rows = [...root.querySelectorAll("[data-template-row]")];
		if (rows.length === 0) return dropIndexRef.current;
		for (let i = 0; i < rows.length; i++) {
			const rect = rows[i].getBoundingClientRect();
			if (clientY < rect.top + rect.height / 2) return i;
		}
		return rows.length;
	};

	const applyTemplateDrop = (toIndex) => {
		const name = dragTemplateNameRef.current;
		const target = toIndex == null ? dropIndexRef.current : toIndex;
		dragTemplateNameRef.current = null;
		dropIndexRef.current = null;
		setDragTemplateName(null);
		setDropIndex(null);
		if (!name || target == null) return;
		const fromIndex = includedTemplates.indexOf(name);
		if (fromIndex < 0) return;
		onChange(reorderTemplates(dataToSave, fromIndex, target));
	};

	const finishTemplateDrag = () => {
		// Keep the last suggested slot even if the pointer left the list before release.
		if (dragTemplateNameRef.current != null && dropIndexRef.current != null) {
			applyTemplateDrop(dropIndexRef.current);
			return;
		}
		dragTemplateNameRef.current = null;
		dropIndexRef.current = null;
		setDragTemplateName(null);
		setDropIndex(null);
	};

	const availableToAdd = useMemo(() => {
		const all = Object.keys(catalog || {});
		const included = new Set(includedTemplates);
		const q = templateQuery.trim().toLowerCase();
		return all
			.filter((name) => !included.has(name))
			.filter((name) => !q || name.toLowerCase().includes(q))
			.sort((a, b) => a.localeCompare(b));
	}, [catalog, includedTemplates, templateQuery]);

	// When focus comes from the preview (or elsewhere), expand ancestors and scroll the tree row into view
	useEffect(() => {
		if (!focusKey) return;

		const templatePart = focusKey.split("|")[0];
		const sectionPart = focusKey.includes("|s:") ? focusKey.split("|f:")[0] : null;

		setExpanded((prev) => {
			const next = new Set(prev);
			if (templatePart) next.add(templatePart);
			if (sectionPart && sectionPart !== templatePart) next.add(sectionPart);
			return next;
		});

		let cancelled = false;
		const frame = requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				if (cancelled) return;
				const root = treeScrollRef.current;
				if (!root) return;
				const target = [...root.querySelectorAll("[data-tree-key]")].find(
					(el) => el.getAttribute("data-tree-key") === focusKey
				);
				if (!target) return;
				target.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
			});
		});
		return () => {
			cancelled = true;
			cancelAnimationFrame(frame);
		};
	}, [focusKey, focusNonce]);

	const toggleExpanded = (key) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	useEffect(() => {
		if (!showAddTemplate) return;
		const frame = requestAnimationFrame(() => searchRef.current?.focus());
		const onKey = (event) => {
			if (event.key !== "Escape") return;
			event.stopImmediatePropagation();
			setShowAddTemplate(false);
		};
		window.addEventListener("keydown", onKey, true);
		return () => {
			cancelAnimationFrame(frame);
			window.removeEventListener("keydown", onKey, true);
		};
	}, [showAddTemplate]);

	const addAndKeepOpen = (name) => {
		if (!catalog[name]) return;
		onChange(addTemplate(dataToSave, name));
		setExpanded((prev) => new Set(prev).add(`t:${name}`));
		onFocus(`t:${name}`);
	};

	const catalogEmpty = Object.keys(catalog || {}).length === 0;

	return (
		<div className="flex flex-col h-full min-h-0 text-sm">
			<div className="shrink-0 flex items-center justify-between gap-2 bg-white pb-2 border-b border-stone-100">
				<p className="text-xs text-stone-500">Drag templates to reorder. Save the report to keep the order.</p>
				<button
					type="button"
					disabled={catalogEmpty}
					onClick={() => {
						setTemplateQuery("");
						setShowAddTemplate(true);
					}}
					className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-amber-500 text-stone-900 font-medium hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed">
					<IoAdd className="text-lg" />
					Add template
				</button>
			</div>

			<div
				ref={treeScrollRef}
				className="flex-1 min-h-0 overflow-y-auto overscroll-contain pt-2"
				onDragOver={(e) => {
					if (!dragTemplateNameRef.current) return;
					e.preventDefault();
					e.dataTransfer.dropEffect = "move";
					updateDropIndex(slotForClientY(e.clientY));
				}}
				onDrop={(e) => {
					if (!dragTemplateNameRef.current) return;
					e.preventDefault();
					applyTemplateDrop(slotForClientY(e.clientY));
				}}>
				<div className="flex flex-col gap-0.5 min-h-full pb-8">
					{catalogEmpty ? (
						<p className="text-sm text-stone-500 p-2">No project templates available.</p>
					) : includedTemplates.length === 0 ? (
						<p className="text-sm text-stone-500 p-2">No templates added yet.</p>
					) : (
						includedTemplates.map((templateName, index) => {
							const sectionNames = getCatalogSectionNames(catalog, templateName);
							const templateKey = `t:${templateName}`;
							const templateOpen = expanded.has(templateKey);
							const templateLatest = getTemplateLatestVersion(catalog, templateName);
							const isDragging = dragTemplateName === templateName;

							return (
								<div
									key={templateName}
									data-template-row
									onDragOver={(e) => {
										e.preventDefault();
										e.stopPropagation();
										e.dataTransfer.dropEffect = "move";
										updateDropIndex(slotForPointer(e, index));
									}}
									onDrop={(e) => {
										e.preventDefault();
										e.stopPropagation();
										applyTemplateDrop(slotForPointer(e, index));
									}}
									className={`border border-stone-200 rounded mb-1 overflow-hidden ${
										dropIndex === index ? "border-t-2 border-t-blue-600" : "border-t-2 border-t-transparent"
									} ${isDragging ? "opacity-50" : ""}`}>
									<div
										data-tree-key={templateKey}
										className={`flex items-center gap-1 px-1 py-1.5 bg-stone-50 ${
											focusKey === templateKey ? "ring-1 ring-inset ring-amber-300 bg-amber-50" : ""
										}`}>
										<span
											draggable
											onDragStart={(e) => {
												e.stopPropagation();
												e.dataTransfer.effectAllowed = "move";
												try {
													e.dataTransfer.setData("text/plain", templateName);
												} catch (_) {
													/* some browsers require data for a reliable drop */
												}
												beginTemplateDrag(templateName);
											}}
											onDragEnd={finishTemplateDrag}
											className="shrink-0 p-0.5 text-stone-400 cursor-grab active:cursor-grabbing"
											title="Drag to reorder"
											aria-label={`Reorder ${templateName}`}>
											<MdDragIndicator />
										</span>
										<button
											type="button"
											className="shrink-0 p-0.5 text-stone-500"
											onClick={() => toggleExpanded(templateKey)}
											aria-label={templateOpen ? "Collapse template" : "Expand template"}>
											{templateOpen ? <MdExpandMore /> : <MdChevronRight />}
										</button>
										<span className="text-[10px] text-stone-400 w-4 shrink-0 text-center">{index + 1}</span>
										<button
											type="button"
											className="flex-1 min-w-0 text-left font-medium text-stone-800 truncate py-0.5"
											onClick={() => {
												onFocus(templateKey);
												if (!templateOpen) toggleExpanded(templateKey);
											}}>
											{templateName}
										</button>
										<VersionBadge version={templateLatest} />
										<button
											type="button"
											className="shrink-0 p-1 text-red-600 hover:bg-red-50 rounded"
											title="Remove template"
											onClick={() => {
												if (!window.confirm(`Remove "${templateName}" from this report?`)) return;
												onChange(removeTemplate(dataToSave, templateName));
											}}>
											<BsTrash className="w-3.5 h-3.5" />
										</button>
									</div>

									{templateOpen ? (
										<div className="pb-1">
											{sectionNames.length === 0 ? (
												<p className="text-xs text-stone-400 px-3 py-1 ml-4">No sections in catalog</p>
											) : (
												sectionNames.map((sectionName) => {
													const fields = getCatalogFieldNames(catalog, templateName, sectionName);
													const sectionKey = `t:${templateName}|s:${sectionName}`;
													const sectionOpen = expanded.has(sectionKey);
													const sectionOn = isSectionIncluded(dataToSave, templateName, sectionName);
													const sectionLatest = getSectionLatestVersion(catalog, templateName, sectionName);

													return (
														<div key={sectionKey} className="ml-3">
															<div
																data-tree-key={sectionKey}
																className={`flex items-center gap-1 rounded px-1 py-1 ${
																	focusKey === sectionKey ? "bg-amber-50 ring-1 ring-amber-200" : "hover:bg-stone-50"
																}`}>
																<button
																	type="button"
																	className="shrink-0 p-0.5 text-stone-500"
																	onClick={() => toggleExpanded(sectionKey)}>
																	{sectionOpen ? <MdExpandMore /> : <MdChevronRight />}
																</button>
																<input
																	type="checkbox"
																	checked={sectionOn}
																	onChange={() => onChange(toggleSection(dataToSave, templateName, sectionName))}
																	className="shrink-0"
																/>
																<button
																	type="button"
																	className="flex-1 min-w-0 text-left text-stone-700 truncate py-0.5"
																	onClick={() => {
																		onFocus(sectionKey);
																		if (!sectionOpen) toggleExpanded(sectionKey);
																	}}>
																	{sectionName}
																</button>
																<VersionBadge version={sectionLatest} />
															</div>

															{sectionOpen
																? fields.map((fieldName) => {
																		const fieldKey = `t:${templateName}|s:${sectionName}|f:${fieldName}`;
																		const fieldOn = isFieldIncluded(dataToSave, templateName, sectionName, fieldName);
																		const fieldLatest = getFieldLatestVersion(
																			catalog,
																			templateName,
																			sectionName,
																			fieldName
																		);
																		return (
																			<div
																				key={fieldKey}
																				data-tree-key={fieldKey}
																				className={`ml-4 flex items-center gap-1 rounded px-1 py-1 ${
																					focusKey === fieldKey
																						? "bg-amber-50 ring-1 ring-amber-200"
																						: "hover:bg-stone-50"
																				}`}>
																				<span className="w-5 shrink-0" />
																				<input
																					type="checkbox"
																					checked={fieldOn}
																					onChange={() => {
																						const turningOn = !fieldOn;
																						onChange(toggleField(dataToSave, templateName, sectionName, fieldName));
																						if (turningOn) onFocus(fieldKey);
																					}}
																					className="shrink-0"
																				/>
																				<button
																					type="button"
																					className="flex-1 min-w-0 text-left text-stone-600 truncate py-0.5"
																					onClick={() => onFocus(fieldKey)}>
																					{fieldName}
																				</button>
																				<VersionBadge version={fieldLatest} />
																			</div>
																		);
																  })
																: null}
														</div>
													);
												})
											)}
										</div>
									) : null}
								</div>
							);
						})
					)}
					{includedTemplates.length > 0 ? (
						<div
							className={`shrink-0 h-8 ${
								dropIndex === includedTemplates.length ? "border-t-2 border-blue-600" : "border-t-2 border-transparent"
							}`}
							onDragOver={(e) => {
								e.preventDefault();
								e.stopPropagation();
								e.dataTransfer.dropEffect = "move";
								updateDropIndex(includedTemplates.length);
							}}
							onDrop={(e) => {
								e.preventDefault();
								e.stopPropagation();
								applyTemplateDrop(includedTemplates.length);
							}}
						/>
					) : null}
				</div>
			</div>

			{showAddTemplate ? (
				<div
					className="fixed inset-0 z-[400] bg-black/60 flex items-center justify-center p-4"
					onMouseDown={() => setShowAddTemplate(false)}>
					<div
						className="w-full max-w-md bg-white text-stone-900 rounded-xl shadow-2xl border border-stone-200 flex flex-col max-h-[70dvh] overflow-hidden"
						onMouseDown={(event) => event.stopPropagation()}>
						<div className="shrink-0 flex items-center justify-between gap-3 px-4 py-3 border-b border-stone-100">
							<p className="text-2xl font-semibold">Add template</p>
							<button
								type="button"
								onClick={() => setShowAddTemplate(false)}
								className="text-sm px-3 py-1 rounded bg-stone-100 hover:bg-stone-200">
								Close
							</button>
						</div>
						<div className="shrink-0 p-3">
							<input
								ref={searchRef}
								type="text"
								value={templateQuery}
								onChange={(event) => setTemplateQuery(event.target.value)}
								placeholder="Search by name"
								className="w-full border border-stone-300 rounded-md px-3 py-2 text-sm"
							/>
						</div>
						<div className="shrink-0 px-4 py-2 bg-stone-100 border-y border-stone-200 flex items-center gap-3">
							<span className="shrink-0 text-[11px] uppercase tracking-wide font-bold text-stone-600">Legend</span>
							<div className="flex flex-wrap gap-x-2 gap-y-1">
								{TEMPLATE_CHIPS.map((chip) => (
									<span key={chip.label} className={`rounded px-2 py-0.5 text-xs font-medium ${chip.className}`}>
										{chip.label}
									</span>
								))}
							</div>
						</div>
						<ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-stone-300">
							{availableToAdd.length === 0 ? (
								<li className="px-4 py-6 text-sm text-stone-500">
									{templateQuery.trim() ? "No templates match." : "Every template is already on this report."}
								</li>
							) : (
								availableToAdd.map((name) => {
									const details = getTemplateDetails(catalog, name);
									const chips = [
										details.templateType ? { label: "Type", value: details.templateType, className: TEMPLATE_CHIPS[0].className } : null,
										details.portfolioType ? { label: "Portfolio", value: details.portfolioType, className: TEMPLATE_CHIPS[1].className } : null,
										details.odooModel ? { label: "Model", value: details.odooModel, className: TEMPLATE_CHIPS[2].className } : null,
									].filter(Boolean);
									return (
										<li key={name}>
											<button
												type="button"
												className="w-full flex flex-col items-stretch gap-1 text-left px-4 py-2.5 hover:bg-amber-50"
												onClick={() => addAndKeepOpen(name)}>
												<span className="flex items-center gap-2 min-w-0">
													<span className="flex-1 min-w-0 truncate">{name}</span>
													<VersionBadge version={getTemplateLatestVersion(catalog, name)} />
												</span>
												{chips.length > 0 ? (
													<span className="flex flex-wrap gap-x-2 gap-y-1">
														{chips.map((chip) => (
															<span
																key={chip.label}
																title={chip.label}
																className={`max-w-full truncate rounded px-2 py-0.5 text-xs font-medium ${chip.className}`}>
																{chip.value}
															</span>
														))}
													</span>
												) : null}
											</button>
										</li>
									);
								})
							)}
						</ul>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default ReportStructureTree;
